import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { MindMapContext } from '../../Context/contex';
import useMindMapService from '../../hooks/useMindMapService';
import { emitNodeDelete, emitNodeUpdate } from '../../Socket/socket';
import { applyValues, mapColors, readValues, savePayloads, socketUpdates, withAspect } from './nodeProps';

/**
 * Edits one node while the panel is open. Changes show on this user's canvas at once but are not saved or sent;
 * `apply` saves them, tells collaborators and adds one undo step; `discard` puts the node back. Unapplied edits
 * are also put back when the panel moves to another node or closes.
 */
export const useNodeProperties = (nodeId) => {
    const { documentId } = useParams();
    const { nodes, edges, setNodes, setEdges, recordChange, setIsDocumentChanged } = MindMapContext();
    const mindMapService = useMindMapService();
    const [values, setValues] = useState(null);
    const [touched, setTouched] = useState(() => new Set());
    const [lockAspect, setLockAspect] = useState(false);
    const original = useRef(null);
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const isDirty = touched.size > 0;
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const node = nodes.find((candidate) => candidate.id === nodeId) ?? null;
    const colors = useMemo(() => (nodeId ? mapColors(nodes) : []), [nodeId, nodes]);

    const replaceNode = useCallback((next) => {
        setNodes((current) => current.map((candidate) => (candidate.id === next.id ? next : candidate)));
    }, [setNodes]);

    useEffect(() => {
        const opened = nodesRef.current.find((candidate) => candidate.id === nodeId);
        if (!opened) return undefined;
        original.current = opened;
        setValues(readValues(opened));
        setTouched(new Set());
        return () => {
            if (isDirtyRef.current) replaceNode(original.current);
        };
    }, [nodeId, replaceNode]);

    const change = (patch) => {
        const requested = lockAspect ? withAspect(patch, values) : patch;
        // Leaving a field without changing it reports its value again; that is not an edit.
        const edit = Object.fromEntries(Object.entries(requested).filter(([key, value]) => values[key] !== value));
        if (Object.keys(edit).length === 0) return;
        const nextValues = { ...values, ...edit };
        const nextTouched = new Set([...touched, ...Object.keys(edit)]);
        setValues(nextValues);
        setTouched(nextTouched);
        if (node) replaceNode(applyValues(node, nextValues, nextTouched));
    };

    // The map as it was before this panel's edits, for undo.
    const mapBeforeEdits = () => ({
        nodes: nodes.map((candidate) => (candidate.id === nodeId ? original.current : candidate)),
        edges,
    });

    const apply = () => {
        if (!isDirty || !node) return;
        const after = { nodes, edges };
        savePayloads(node, touched).forEach((payload) => mindMapService.updateNode(node.id, payload, after));
        socketUpdates(node, touched, documentId).forEach((update) => emitNodeUpdate(update));
        recordChange(mapBeforeEdits(), after);
        setIsDocumentChanged(true);
        original.current = node;
        setTouched(new Set());
    };

    const discard = () => {
        if (!isDirty) return;
        replaceNode(original.current);
        setValues(readValues(original.current));
        setTouched(new Set());
    };

    const remove = () => {
        const remainingEdges = edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);
        const remainingNodes = nodes.filter((candidate) => candidate.id !== nodeId);
        const after = { nodes: remainingNodes, edges: remainingEdges };
        recordChange(mapBeforeEdits(), after);
        setEdges(remainingEdges);
        setNodes(remainingNodes);
        mindMapService.deleteNodeWithEdges(nodeId, edges, after);
        emitNodeDelete({ documentId, selectedNodeId: nodeId });
        setIsDocumentChanged(true);
        setTouched(new Set());
    };

    return { node, values, isDirty, change, apply, discard, remove, lockAspect, setLockAspect, mapColors: colors };
};
